/**
 * MapContent Component
 * Renders polygons on the map based on GeoJSON data and predictions
 */

import React, { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { GeoJsonData, HoverInfo, IPredictionDataModel } from "@/util/http/WeevilTrak/responseType";
import { geoJsonCoordsToLatLng, getColorForValue, getGradientColor, getValue } from "@/page/WeevilTrak/weevilTrakCommonFunc";
import { WeevilTrakTabsEnum } from "@/util/cosntants";
import { clipGeoJsonFeaturesWithCircle } from "../utils/clippingUtils";

interface MapContentProps {
    geoJsonData: GeoJsonData;
    predictionData: IPredictionDataModel[];
    center?: {lat: number;lng: number;} // Circle center for clipping
    currentTab?: WeevilTrakTabsEnum;
    radius?: number; // Circle radius in kilometers for clipping
    onHover: (info: HoverInfo | null) => void;
    autoFitBounds?: boolean;
    onAutoFitStart?: () => void;
    onAutoFitEnd?: () => void;
}

export const MapContent: React.FC<MapContentProps> = ({
    geoJsonData,
    predictionData,
    onHover,
    autoFitBounds = true,
    onAutoFitStart,
    onAutoFitEnd,
    currentTab,
    center,
    radius
}) => {
    const map = useMap();
    const polygonsRef = useRef<google.maps.Polygon[]>([]);
    const polygonDataRef = useRef<Map<google.maps.Polygon, HoverInfo>>(new Map());

    // Auto-fit bounds to show all data points
    useEffect(() => {
        if (!autoFitBounds) return;
        if (!map || geoJsonData.features.length === 0) return;

        const bounds = new google.maps.LatLngBounds();
        geoJsonData.features.forEach(feature => {
            const paths = geoJsonCoordsToLatLng(feature.geometry.coordinates[0]);
            paths.forEach(path => bounds.extend(new google.maps.LatLng(path.lat, path.lng)));
        });

        // Apply bounds with padding
        onAutoFitStart?.();
        map.fitBounds(bounds, 50);
        onAutoFitEnd?.();
    }, [map, geoJsonData, autoFitBounds, onAutoFitStart, onAutoFitEnd]);


    // for render map for region tab
    useEffect(() => {
        if (!map) return;

        console.log("MapContent useEffect - map ready");
        console.log("geoJsonData.features:", geoJsonData.features);
        console.log("predictionData:", predictionData);

        polygonsRef.current?.forEach(polygon => {
            google.maps.event.clearInstanceListeners(polygon);
            polygon.setMap(null);
        });
        polygonsRef.current = [];
        polygonDataRef.current.clear();

        if (!geoJsonData || !geoJsonData.features || geoJsonData.features.length === 0) {
            return;
        }


        // for region tab
        if(currentTab === WeevilTrakTabsEnum.Region ){
            let createdCount = 0;
            geoJsonData.features.forEach(feature => {
                const placeId = feature.properties.place_id;
                const value = getValue(placeId, predictionData);

                console.log(`Feature: ${placeId}, value: ${value}`);

                if (value < 0) {
                    console.log(`Skipping ${placeId} - value < 0`);
                    return;
                }

                createdCount++;
                console.log(`Creating polygon for ${placeId}`);

                const paths = geoJsonCoordsToLatLng(feature.geometry.coordinates[0]);

                console.log(`Polygon paths for ${placeId}:`, paths);

                const bounds = new google.maps.LatLngBounds();
                paths.forEach(path => bounds.extend(new google.maps.LatLng(path.lat, path.lng)));
                const center = bounds.getCenter();

                // const color = getColorForValue(value);
                const rgbValue = getGradientColor(value);
                const color = `rgb(${rgbValue.r}, ${rgbValue.g}, ${rgbValue.b})`;

                const polygon = new google.maps.Polygon({
                    paths: paths,
                    fillColor: color,
                    fillOpacity: 0.7,
                    strokeColor: color,
                    strokeOpacity: 0.9,
                    strokeWeight: 1,
                    zIndex: 1
                });

                const hoverInfo: HoverInfo = {
                    placeId,
                    lat: center.lat(),
                    lng: center.lng(),
                    value,
                    predictionDate: feature?.properties?.prediction_date,
                    predictedStageDate: feature?.properties?.predicted_stage_date,
                    stage: feature?.properties?.stage
                };
                polygonDataRef.current.set(polygon, hoverInfo);

                polygon.addListener("mouseover", (e: any) => {
                    polygon.setOptions({ strokeWeight: 2 ,strokeColor: "yellow",zIndex: 999});
                    onHover({
                        ...hoverInfo,
                        mouseX: e.domEvent.clientX,
                        mouseY: e.domEvent.clientY
                    });
                });

                polygon.addListener("mouseout", () => {
                    polygon.setOptions({
                        strokeWeight: 1 ,
                        strokeColor: color,
                        strokeOpacity: 0.9,
                        zIndex: 1});
                    onHover(null);
                });

                polygon.setMap(map);
                polygonsRef.current.push(polygon);
            });

            console.log(`Total polygons created: ${createdCount}`);
        }
        else if(currentTab === WeevilTrakTabsEnum.LOCATION && center){
            // Clip features with circle if center and radius are provided
            const featuresToRender = (center && radius)
                ? clipGeoJsonFeaturesWithCircle(geoJsonData.features, center, radius)
                : geoJsonData.features;

            console.log("Clipping enabled:", !!(center && radius));
            console.log("Original features:", geoJsonData.features.length);
            console.log("Clipped features:", featuresToRender.length);
            console.log("PredictionData:", predictionData.map(d => ({ location: d.location, predictedDays: d.predictedDays })));


            let createdCount = 0;
            featuresToRender.forEach(feature => {
                const placeId = feature.properties.place_id;
                const value = getValue(placeId, predictionData);

                console.log(`Feature: ${placeId}, value: ${value}`);

                if (value < 0) {
                    console.log(`Skipping ${placeId} - value <   0`);
                    return;
                }

                createdCount++;
                console.log(`Creating polygon for ${placeId}`);

                console.log("Feature geometry type:", feature.geometry.type);
                console.log("Feature coordinates:", feature.geometry.coordinates);

                // Handle different coordinate structures
                let pathsArray: number[][][];
                if (feature.geometry.type === "Polygon") {
                // Turf.js returns coordinates as array of rings, each ring is an array of [lng, lat] pairs
                    pathsArray = feature.geometry.coordinates as number[][][];
                } else {
                // Fallback for other geometry types
                    pathsArray = [feature.geometry.coordinates[0]] as number[][][];
                }

                // const paths = geoJsonCoordsToLatLng(feature.geometry.coordinates[0]);
                // Use the first ring (outer boundary) for the polygon
                const paths = geoJsonCoordsToLatLng(pathsArray[0]);

                console.log(`Polygon paths for ${placeId}:`, paths);
                console.log("Paths length:", paths.length);

                const bounds = new google.maps.LatLngBounds();
                paths.forEach(path => bounds.extend(new google.maps.LatLng(path.lat, path.lng)));
                const center = bounds.getCenter();

                // const color = getColorForValue(value);
                const rgbValue = getGradientColor(value);
                const color = `rgb(${rgbValue.r}, ${rgbValue.g}, ${rgbValue.b})`;

                const polygon = new google.maps.Polygon({
                    paths: paths,
                    fillColor: color,
                    fillOpacity: 0.7,
                    strokeColor: color,
                    strokeOpacity: 0.9,
                    strokeWeight: 1,
                    zIndex: 1
                });

                const hoverInfo: HoverInfo = {
                    placeId,
                    lat: center.lat(),
                    lng: center.lng(),
                    value,
                    predictionDate: feature?.properties?.prediction_date,
                    predictedStageDate: feature?.properties?.predicted_stage_date,
                    stage: feature?.properties?.stage
                };
                polygonDataRef.current.set(polygon, hoverInfo);

                polygon.addListener("mouseover", (e: any) => {
                    polygon.setOptions({ strokeWeight: 2 ,strokeColor: "yellow",zIndex: 100});
                    onHover({
                        ...hoverInfo,
                        mouseX: e.domEvent.clientX,
                        mouseY: e.domEvent.clientY
                    });
                });

                polygon.addListener("mouseout", () => {
                    polygon.setOptions({
                        strokeWeight: 1 ,
                        strokeColor: color,
                        strokeOpacity: 0.9,
                        zIndex: 1});
                    onHover(null);
                });

                polygon.setMap(map);
                polygonsRef.current.push(polygon);
            });

            console.log(`Total polygons created: ${createdCount}`);


        }

        const currentPolygons = polygonsRef.current;

        return () => {
            currentPolygons.forEach(polygon => {
                google.maps.event.clearInstanceListeners(polygon);
                polygon.setMap(null);
            });
        };
    }, [map, geoJsonData, predictionData, onHover]);

    return null;
};
